<template>
  <el-dialog :visible="visible" title="商品第二应用管理" width="700px" top="6vh" append-to-body custom-class="doudian-product-manager" :close-on-click-modal="false" @close="onClose">
    <div class="dpm-context">
      <strong>{{ shop && shop.shop_name }}</strong>
      <span>当前铺货：{{ currentName }}</span>
    </div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <el-alert v-if="notice" :title="notice" type="success" :closable="false" show-icon />
    <div v-if="!mainReady" class="dpm-warning">
      <strong>请先处理主商品应用</strong>
      <span>主应用订购有效且授权正常后，才能设置第二应用用于铺货。</span>
      <el-button size="mini" :disabled="!defaultServiceId || busy" @click="$emit('authorize', { appType: 'product' })">处理主应用</el-button>
    </div>
    <p v-if="shop && shop.distribution_allowed === false && shop.distribution_block_reason" class="dpm-reason">{{ shop.distribution_block_reason }}</p>
    <div v-loading="loading" class="dpm-apps" element-loading-text="正在刷新应用状态">
      <div v-for="app in applications" :key="app.service_id" class="dpm-app" :class="{ 'is-current': isCurrent(app) }" :data-service-id="app.service_id">
        <div class="dpm-app-info">
          <div class="dpm-app-title"><strong>{{ app.app_name }}</strong><span v-if="isCurrent(app)" class="dpm-current">当前铺货应用</span></div>
          <div class="dpm-state" :class="'dpm-state--' + state(app).tone">{{ state(app).text }}</div>
          <div class="dpm-expiry">服务到期：{{ expireText(app) }}</div>
        </div>
        <div class="dpm-app-actions">
          <template v-if="!app.unavailable">
            <el-button v-if="!isCurrent(app) && state(app).ok" type="primary" size="small" :disabled="!canSelect(app)" :loading="saving === app.service_id" @click="$emit('select', app.service_id)">用于铺货</el-button>
            <el-button size="small" :disabled="busy" @click="$emit('authorize', { appType: 'secondary', serviceId: app.service_id })">{{ authorizeText(app) }}</el-button>
            <span v-if="!isCurrent(app) && state(app).ok && !canSelect(app)" class="dpm-help">{{ !mainReady ? '请先处理主商品应用' : '当前状态未确认，请刷新后重试' }}</span>
          </template>
          <span v-else class="dpm-help">应用配置已失效，请处理或改选其他应用</span>
        </div>
      </div>
      <div v-if="!apps.length" class="dpm-empty">暂无可用的第二商品应用配置，请联系管理员。</div>
    </div>
    <div v-if="showPrimarySwitch" class="dpm-primary-switch">
      <el-button size="small" :disabled="!canSwitchPrimary" :loading="!!saving && saving === defaultServiceId" @click="$emit('select', defaultServiceId)">使用主应用铺货</el-button>
      <span>{{ primarySwitchReason }}</span>
    </div>
    <template #footer>
      <el-button size="small" :disabled="busy || !shop" @click="$emit('check-distribution')">铺货检测</el-button>
      <el-button size="small" :loading="loading" :disabled="!!saving" @click="$emit('refresh')">刷新状态</el-button>
      <el-button size="small" @click="$emit('close')">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { distributionServiceId, distributionApplicationName, hasApplicationPolicy, usesPrimaryApplication, mainApplicationReady, secondaryApplicationState } from './doudianProductApplicationState'

export default {
  name: 'DoudianProductApplicationManager',
  props: {
    visible: Boolean,
    shop: { type: Object, default: null },
    apps: { type: Array, default: () => [] },
    defaultServiceId: { type: String, default: '' },
    now: { type: Number, required: true },
    loading: Boolean,
    saving: { type: String, default: '' },
    error: { type: String, default: '' },
    notice: { type: String, default: '' }
  },
  computed: {
    currentName() { return distributionApplicationName(this.shop, this.apps, this.defaultServiceId) },
    mainReady() { return mainApplicationReady(this.shop, this.now) },
    busy() { return this.loading || !!this.saving },
    applications() {
      const selected = distributionServiceId(this.shop)
      return selected && selected !== this.defaultServiceId && !this.apps.some(app => app.service_id === selected)
        ? [...this.apps, { service_id: selected, app_name: `应用 ${selected}`, unavailable: true }] : this.apps
    },
    showPrimarySwitch() { return !!this.shop && !usesPrimaryApplication(this.shop, this.defaultServiceId) },
    canSwitchPrimary() { return !this.busy && !!this.defaultServiceId && this.mainReady && hasApplicationPolicy(this.shop) && this.shop.secondary_product_subscribed === false },
    primarySwitchReason() {
      if (!hasApplicationPolicy(this.shop)) return '状态暂不可用，请刷新后重试'
      if (this.shop.secondary_product_subscribed === true) return '存在有效第二应用订购，须指定第二应用铺货'
      if (this.shop.secondary_product_subscribed === null) return '第二应用权益待确认，暂不能切回主应用'
      return this.mainReady ? '没有有效第二应用订购时，可明确切回主应用' : '请先处理主商品应用'
    }
  },
  methods: {
    onClose() { if (this.visible) this.$emit('close') },
    isCurrent(app) { return distributionServiceId(this.shop) === app.service_id },
    state(app) { return app.unavailable ? { key: 'unavailable', text: '当前不可用', tone: 'warning', ok: false, expiry: null } : secondaryApplicationState(this.shop, app.service_id, this.now) },
    canSelect(app) { return !this.busy && this.mainReady && hasApplicationPolicy(this.shop) && this.shop.secondary_product_subscribed === true && this.state(app).ok },
    expireText(app) {
      const state = this.state(app)
      if (!state.expiry) return state.key === 'unauthorized' ? '--' : state.key === 'unavailable' ? '状态暂不可用' : '待确认'
      const date = new Date(state.expiry * 1000)
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    authorizeText(app) { return { unauthorized: '去订购/授权', expired: '去续订', abnormal: '重新授权' }[this.state(app).key] || '授权/续订' }
  }
}
</script>

<style>
.doudian-product-manager.el-dialog { max-width: calc(100vw - 24px); max-height: 88vh; display: flex; flex-direction: column; border-radius: 10px; }
.doudian-product-manager .el-dialog__body { overflow-y: auto; padding: 12px 22px 20px; }
.doudian-product-manager .el-dialog__header, .doudian-product-manager .el-dialog__footer { flex-shrink: 0; }
.doudian-product-manager .el-alert { margin: 10px 0; }
.dpm-context { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-bottom: 16px; font-size: 13px; overflow-wrap: anywhere; color: #303744; }
.dpm-context span { color: #606978; }
.dpm-warning { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px; border-radius: 6px; background: #fff7eb; color: #a76026; font-size: 12px; }
.dpm-warning span { flex: 1 1 220px; }
.dpm-reason { margin: 10px 0; color: #b85d45; font-size: 12px; overflow-wrap: anywhere; }
.dpm-app { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 12px; margin-top: 10px; border: 1px solid #e4e7ee; border-radius: 7px; }
.dpm-app.is-current { border-color: #a7ceb0; background: #f7fbf8; }
.dpm-app-info { min-width: 0; flex: 1; }
.dpm-app-title { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 14px; overflow-wrap: anywhere; }
.dpm-current { padding: 2px 6px; border-radius: 3px; font-size: 11px; color: #427650; background: #e5f2e8; }
.dpm-state { margin-top: 9px; font-size: 12px; }
.dpm-state--success { color: #438951; }
.dpm-state--warning { color: #bf6045; }
.dpm-state--muted { color: #858d99; }
.dpm-expiry, .dpm-help { color: #858d99; font-size: 12px; line-height: 1.6; margin-top: 4px; }
.dpm-app-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; flex: 0 1 220px; }
.dpm-app-actions .el-button + .el-button { margin-left: 0; }
.dpm-help { flex-basis: 100%; text-align: right; overflow-wrap: anywhere; }
.dpm-empty { padding: 30px 12px; text-align: center; color: #858d99; font-size: 13px; }
.dpm-primary-switch { margin-top: 18px; border-top: 1px solid #eceef2; padding-top: 14px; display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.dpm-primary-switch span { color: #858d99; font-size: 12px; flex: 1 1 210px; }
@media (max-width: 560px) {
  .doudian-product-manager .el-dialog__body { padding: 10px 14px 16px; }
  .dpm-app { align-items: stretch; flex-direction: column; gap: 10px; }
  .dpm-app-actions { flex: auto; justify-content: flex-start; }
  .dpm-help { text-align: left; }
}
</style>
